import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/mutations"

import {
  type InventoryPalletHoldFailure,
  type InventoryPalletHoldInput,
  ReleasePalletHoldDocument,
  type ReleasePalletHoldMutation,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseReleasePalletHoldReturn {
  releasePalletHold: (input: InventoryPalletHoldInput) => Promise<boolean>
  isReleasing: boolean
}

export const useReleasePalletHold = (): UseReleasePalletHoldReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<ReleasePalletHoldMutation, { input: InventoryPalletHoldInput }>(
    ReleasePalletHoldDocument
  )

  const releasePalletHold = useCallback(
    async (input: InventoryPalletHoldInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.inventory.releasePalletHold
      const hasError = error || output?.__typename === "InventoryPalletHoldFailure"

      if (hasError) {
        const message = error?.message ?? (output as InventoryPalletHoldFailure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      notify({ type: NotificationType.Success, message: "Pallet hold released" })

      return true
    },
    [executeMutation, notify]
  )

  return { releasePalletHold, isReleasing: fetching }
}
